import { Controller } from "@hotwired/stimulus"

// Slide-over preview drawer for the company roster. Opening a row clones its
// pre-rendered <template> into the panel — no network, no spinner, instant. The
// shell animates in on its own (compositor-only transform/opacity), so the click
// never lands on a dead box. ↑/↓ page through the roster without leaving the
// drawer; Esc, the backdrop, and the close button dismiss it. Body scroll is
// locked and Tab is trapped while open; focus returns to the opening row on close.
export default class extends Controller {
  static targets = ["root", "backdrop", "panel", "content", "row"]
  static values = { skipDeepLink: Boolean }

  connect() {
    this.index = -1
    this.remote = false
    // Lazy previews are fetched once per person and cached for the session, so ↑/↓
    // paging and re-opens stay instant. loadToken guards against a slow response
    // landing after the user has already paged on to someone else.
    this.cache = new Map()
    this.pending = new Map()
    this.loadToken = 0
    this.onKeydown = this.onKeydown.bind(this)

    // Deep link (?preview=ID) — a name clicked in the weekly briefing email lands
    // here and opens that person's drawer. Consume the param (replaceState) so a
    // refresh won't re-open it and a second drawer on the page can't double-fire.
    // A scope can opt out (skipDeepLink) so it never steals the param from the
    // drawer that opens the person zero-network with paging (the activity feed),
    // when it happens to sit earlier in the DOM (the dashboard action queue).
    const id = new URLSearchParams(window.location.search).get("preview")
    if (id && !this.skipDeepLinkValue) {
      const url = new URL(window.location.href)
      url.searchParams.delete("preview")
      window.history.replaceState({}, "", url)
      this.openProfile(id)
    }
  }

  disconnect() {
    this.unlock()
    document.removeEventListener("keydown", this.onKeydown)
    clearTimeout(this.hideTimer)
    clearTimeout(this.fadeTimer)
  }

  open(event) {
    // Space/Enter on a focused row would otherwise scroll/submit — swallow it.
    if (event.type === "keydown") event.preventDefault()
    const i = this.rowTargets.indexOf(event.currentTarget)
    if (i < 0) return
    this.opener = event.currentTarget
    this.render(i, false)
    this.show()
  }

  // Open by profile id from a link (in-app name) rather than a row click.
  openLink(event) {
    event.preventDefault()
    this.openProfile(event.params.profile)
  }

  // Keep a click/keydown on a row's own CTA (the external outreach link in an
  // action-queue row) from bubbling to the whole-row open handler — the link
  // keeps its native default (opening LinkedIn in a new tab).
  stop(event) { event.stopPropagation() }

  // Warm a row preview when intent is likely (pointer hover or keyboard focus).
  // fetchPreview de-duplicates through the session map, so opening the row reuses
  // the same promise/result and never creates request amplification.
  prefetch(event) {
    const id = event.currentTarget?.dataset?.rosterDrawerProfileId
    if (id) this.fetchPreview(String(id))
  }

  // Open the drawer for an arbitrary profile id. Fast path: if that person is
  // already a row on this page, render their row — paging stays live. Otherwise
  // fetch the rendered preview (a deep-linked / off-page name) and inject it;
  // paging is disabled for a fetched person since they have no place in the row
  // sequence. Fetch-then-show so a network hiccup never opens a dead drawer.
  async openProfile(id) {
    if (!id) return
    const key = String(id)
    const row = this.rowTargets.find((r) => r.dataset.rosterDrawerProfileId === key)
    if (row) {
      this.opener = row
      this.render(this.rowTargets.indexOf(row), false)
      this.show()
      return
    }
    this.opener = null
    const html = await this.fetchPreview(key)
    if (html == null) return // no broken half-open drawer on a transient fetch failure
    this.index = -1
    this.remote = true
    this.contentTarget.classList.remove("opacity-0")
    this.contentTarget.replaceChildren(this.fragmentFrom(html))
    this.show()
  }

  show() {
    clearTimeout(this.hideTimer)
    this.rootTarget.classList.remove("hidden")
    document.addEventListener("keydown", this.onKeydown)
    this.lock()
    // Flush the from-state (display:none → visible, still off-screen) with a
    // synchronous reflow so the transition has a start point to animate from.
    // A reflow — not requestAnimationFrame — because rAF is paused in background
    // tabs, which would leave the panel stuck off-screen.
    void this.panelTarget.offsetWidth
    this.panelTarget.classList.remove("translate-x-full")
    this.backdropTarget.classList.remove("opacity-0")
    this.contentTarget.classList.remove("opacity-0")
    this.panelTarget.focus()
  }

  close() {
    document.removeEventListener("keydown", this.onKeydown)
    this.panelTarget.classList.add("translate-x-full")
    this.backdropTarget.classList.add("opacity-0")
    this.contentTarget.classList.add("opacity-0")
    // Hide after the slide-out. A timer (not transitionend) so reduced-motion,
    // where there's no transition to end, still cleans up.
    clearTimeout(this.hideTimer)
    this.hideTimer = setTimeout(() => this.rootTarget.classList.add("hidden"), 320)
    this.unlock()
    if (this.opener) this.opener.focus()
  }

  // Paging walks the page's rows; a fetched (remote) person isn't in that sequence,
  // so ↑/↓ no-op until the next row-based open.
  next() { if (!this.remote && this.index < this.rowTargets.length - 1) this.render(this.index + 1, true) }
  prev() { if (!this.remote && this.index > 0) this.render(this.index - 1, true) }

  // Show row i's preview. A row either carries its preview inline as a <template>
  // (the capped rails — zero network, instant clone) or only a profile id, in which
  // case the preview is fetched on demand and cached (the large roster + activity
  // renders, which would otherwise inline a heavy <template> per row and blow the
  // page's memory). crossfade pages person→person without re-animating the shell;
  // the initial open leaves the fade-in to show().
  render(i, crossfade) {
    const row = this.rowTargets[i]
    if (!row) return
    this.index = i
    this.remote = false
    const tpl = row.querySelector("template")
    if (tpl) {
      this.swap(() => tpl.content.cloneNode(true), crossfade)
    } else if (row.dataset.rosterDrawerProfileId) {
      this.swapLazy(row.dataset.rosterDrawerProfileId)
    }
  }

  // Replace panel content, optionally cross-fading (fade out → swap → fade in).
  // setTimeout (not rAF) so a backgrounded tab still fires the swap/fade-in and
  // can't strand the content invisible.
  swap(makeNode, crossfade) {
    const apply = () => this.contentTarget.replaceChildren(makeNode())
    if (crossfade) {
      this.contentTarget.classList.add("opacity-0")
      clearTimeout(this.fadeTimer)
      this.fadeTimer = setTimeout(() => {
        apply()
        this.contentTarget.classList.remove("opacity-0")
      }, 150)
    } else {
      apply()
    }
  }

  // Fetch-and-inject a row's preview: a skeleton during the (first-time only)
  // network wait, then the content. A token guard drops a stale response that
  // resolves after the user has paged on; cached responses inject instantly.
  async swapLazy(id) {
    const token = ++this.loadToken
    this.contentTarget.classList.remove("opacity-0")
    if (!this.cache.has(id)) this.contentTarget.replaceChildren(this.skeleton())
    const html = await this.fetchPreview(id)
    if (token !== this.loadToken) return
    this.contentTarget.replaceChildren(html != null ? this.fragmentFrom(html) : this.errorNode())
  }

  // One request per person, cached for the session. Returns the HTML string, or
  // null on any failure (not cached, so the next open retries).
  async fetchPreview(id) {
    if (this.cache.has(id)) return this.cache.get(id)
    if (this.pending.has(id)) return this.pending.get(id)
    const request = (async () => {
      try {
        const res = await fetch(`/account/profile_previews/${encodeURIComponent(id)}`, {
          headers: {Accept: "text/html"}
        })
        if (!res.ok) return null
        const html = await res.text()
        this.cache.set(id, html)
        return html
      } catch (_e) {
        return null
      } finally {
        this.pending.delete(id)
      }
    })()
    this.pending.set(id, request)
    return request
  }

  fragmentFrom(html) {
    const tpl = document.createElement("template")
    tpl.innerHTML = html
    return tpl.content.cloneNode(true)
  }

  // Lightweight placeholder shown only while a preview is first fetched (matches
  // the drawer's identity → read → trail rhythm). All-common utility classes, so
  // Tailwind keeps them in the build.
  skeleton() {
    const el = document.createElement("div")
    el.className = "p-5 space-y-4 animate-pulse"
    el.innerHTML =
      '<div class="flex items-center gap-3">' +
      '<div class="w-12 h-12 rounded-full bg-slate-200"></div>' +
      '<div class="flex-1 space-y-2"><div class="h-4 w-2/3 rounded bg-slate-200"></div><div class="h-3 w-1/2 rounded bg-slate-100"></div></div>' +
      "</div>" +
      '<div class="h-20 rounded-lg bg-slate-100"></div>' +
      '<div class="space-y-2"><div class="h-3 rounded bg-slate-100"></div><div class="h-3 w-5/6 rounded bg-slate-100"></div><div class="h-3 w-4/6 rounded bg-slate-100"></div></div>'
    return el
  }

  errorNode() {
    const el = document.createElement("div")
    el.className = "p-8 text-center text-sm text-slate-500"
    el.textContent = "Couldn't load this preview. Close and try again."
    return el
  }

  onKeydown(event) {
    switch (event.key) {
      case "Escape": event.preventDefault(); this.close(); break
      case "ArrowDown": event.preventDefault(); this.next(); break
      case "ArrowUp": event.preventDefault(); this.prev(); break
      case "Tab": this.trapFocus(event); break
    }
  }

  // Keep Tab within the panel while the dialog is open.
  trapFocus(event) {
    const focusable = this.panelTarget.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  lock() { document.body.style.overflow = "hidden" }
  unlock() { document.body.style.overflow = "" }
}
